import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { BuiltBrandLockup } from './ui/BuiltBrandLockup';
import GlobalSearch from './search/GlobalSearch';
import { NotificationBell } from './notifications/NotificationCenter';
import { BROWSE_DESTINATIONS } from '../navigation/browseDestinations';

const AppHeader: React.FC = () => {
  const { user, isAuthenticated, isSuperAdmin, isOrganizer, isJudge, logout, sessionExpiringSoon, timeUntilExpiry } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);

  const primaryLinks = BROWSE_DESTINATIONS.filter(
    (item) => item.href !== '/' || isAuthenticated
  ).map((item) => ({ name: item.name, to: item.href }));

  // Role tool accents match vehicle KPI strip: Cost (amber), HP (orange), RPM (coral/red).
  const toolLinks = [
    ...(isOrganizer
      ? [{ name: 'Organizer', to: '/organizer', accent: 'text-orange-300', accentActive: 'text-orange-200', bar: 'bg-orange-300' }]
      : []),
    ...(isJudge
      ? [{ name: 'Judge', to: '/judge', accent: 'text-orange-400', accentActive: 'text-orange-300', bar: 'bg-orange-400' }]
      : []),
    ...(isSuperAdmin
      ? [{ name: 'Admin', to: '/admin', accent: 'text-red-400', accentActive: 'text-red-300', bar: 'bg-red-400' }]
      : []),
  ];

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const favoritesActive = isActive('/favorites');

  const formatTimeUntilExpiry = () => {
    if (!timeUntilExpiry) return '';
    const minutes = Math.floor(timeUntilExpiry / 60000);
    const seconds = Math.floor((timeUntilExpiry % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (accountRef.current && !accountRef.current.contains(t)) setAccountOpen(false);
      if (toolsRef.current && !toolsRef.current.contains(t)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setAccountOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  const profilePath = user?.username ? `/${user.username}` : '/';

  const utilityCluster = (
    <div className="flex items-center gap-0.5" aria-label="Quick actions">
      {isAuthenticated ? (
        <Link
          to="/favorites"
          aria-label="Favorites"
          aria-current={favoritesActive ? 'page' : undefined}
          className={`btn-icon min-h-[44px] min-w-[44px] ${
            favoritesActive ? 'text-orange-300' : ''
          }`}
          title="Favorites"
        >
          {favoritesActive ? (
            <HeartIconSolid className="w-6 h-6" aria-hidden />
          ) : (
            <HeartIcon className="w-6 h-6" aria-hidden />
          )}
        </Link>
      ) : null}
      <GlobalSearch variant="icon" />
      {isAuthenticated ? <NotificationBell /> : null}
    </div>
  );

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/[0.06]"
        style={{ backgroundColor: 'rgba(10,10,10,0.92)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-lg min-w-0 cursor-pointer"
              aria-label="Bilt:Spec home"
            >
              <BuiltBrandLockup heightClass="h-8 sm:h-9" showBeta />
            </button>

            <nav className="hidden md:flex items-center gap-1 min-w-0 flex-1 justify-end" aria-label="Main navigation">
              {primaryLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.to}
                  aria-current={isActive(link.to) ? 'page' : undefined}
                  className={isActive(link.to) ? 'nav-link-active' : 'nav-link-inactive'}
                >
                  {link.name}
                </Link>
              ))}

              {toolLinks.length > 0 && (
                <div className="relative" ref={toolsRef}>
                  <button
                    type="button"
                    onClick={() => setToolsOpen((o) => !o)}
                    className={
                      toolLinks.some((l) => isActive(l.to)) ? 'nav-link-active' : 'nav-link-inactive'
                    }
                    aria-expanded={toolsOpen}
                    aria-haspopup="menu"
                  >
                    Tools
                    <svg className="w-3.5 h-3.5 opacity-70" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {toolsOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-48 rounded-xl border border-white/[0.1] shadow-elevated py-1 z-50"
                      style={{ backgroundColor: '#0f0f0f' }}
                    >
                      {toolLinks.map((link) => {
                        const active = isActive(link.to);
                        return (
                          <Link
                            key={link.name}
                            role="menuitem"
                            to={link.to}
                            onClick={() => setToolsOpen(false)}
                            aria-current={active ? 'page' : undefined}
                            className={`flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-white/[0.04] ${
                              active ? link.accentActive : link.accent
                            }`}
                          >
                            <span className={`h-2 w-2 shrink-0 rounded-full ${link.bar}`} aria-hidden />
                            {link.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {sessionExpiringSoon && isAuthenticated && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm font-medium ml-1">
                  <span>{formatTimeUntilExpiry()}</span>
                </div>
              )}

              <div className="w-px h-6 bg-white/[0.1] mx-1.5" aria-hidden />

              {utilityCluster}

              {isAuthenticated ? (
                <div className="relative ml-0.5" ref={accountRef}>
                  <button
                    type="button"
                    onClick={() => setAccountOpen((o) => !o)}
                    className={`${
                      isActive(profilePath) ? 'nav-link-active' : 'nav-link-inactive'
                    } min-h-[40px]`}
                    aria-expanded={accountOpen}
                    aria-haspopup="menu"
                    aria-label="Account menu"
                  >
                    <span className="max-w-[8rem] truncate">@{user?.username || 'Account'}</span>
                    <svg className="w-3.5 h-3.5 opacity-70" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {accountOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-52 rounded-xl border border-white/[0.1] shadow-elevated py-1 z-50"
                      style={{ backgroundColor: '#0f0f0f' }}
                    >
                      <Link
                        role="menuitem"
                        to={profilePath}
                        onClick={() => setAccountOpen(false)}
                        className="block px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      >
                        Public profile
                      </Link>
                      <Link
                        role="menuitem"
                        to="/passport"
                        onClick={() => setAccountOpen(false)}
                        className="block px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      >
                        Bilt:Spec Passport
                      </Link>
                      <Link
                        role="menuitem"
                        to="/settings"
                        onClick={() => setAccountOpen(false)}
                        className="block px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      >
                        Settings
                      </Link>
                      <Link
                        role="menuitem"
                        to="/settings/billing"
                        onClick={() => setAccountOpen(false)}
                        className="block px-3 py-2.5 text-sm text-gray-200 hover:bg-white/[0.04]"
                      >
                        Bilt:Spec Premium
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        data-testid="logout-button"
                        onClick={() => {
                          setAccountOpen(false);
                          logout();
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="login-button"
                    className="btn-ghost btn-sm ml-0.5 min-h-[40px]"
                  >
                    Sign in
                  </Link>
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="btn-primary btn-sm ml-1"
                  >
                    Start your garage
                  </button>
                </>
              )}
            </nav>

            <div className="flex md:hidden items-center gap-0.5 shrink-0">
              {utilityCluster}
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  data-testid="login-button-mobile"
                  className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-lg text-sm font-medium text-white border border-white/[0.14] hover:border-white/25 hover:bg-white/[0.06] transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40"
                >
                  Sign in
                </Link>
              ) : null}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="btn-icon min-h-[44px] min-w-[44px]"
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className="md:hidden border-t border-white/[0.06] backdrop-blur-xl px-4 py-4 animate-slide-down"
            style={{ backgroundColor: 'rgba(10,10,10,0.98)' }}
          >
            <nav className="flex flex-col gap-1.5" aria-label="Mobile navigation">
              {primaryLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  aria-current={isActive(link.to) ? 'page' : undefined}
                  className={isActive(link.to) ? 'nav-link-active' : 'nav-link-inactive'}
                >
                  {link.name}
                </Link>
              ))}

              {toolLinks.length > 0 && (
                <div className="pt-2 mt-1 border-t border-white/[0.06]">
                  <p className="px-3 py-1 text-[11px] uppercase tracking-wider text-gray-500">Tools</p>
                  {toolLinks.map((link) => {
                    const active = isActive(link.to);
                    return (
                      <Link
                        key={link.name}
                        to={link.to}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={active ? 'page' : undefined}
                        className={`flex items-center gap-2.5 ${
                          active ? `nav-link-active ${link.accentActive}` : `nav-link-inactive ${link.accent}`
                        }`}
                      >
                        <span className={`h-2 w-2 shrink-0 rounded-full ${link.bar}`} aria-hidden />
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              )}

              {sessionExpiringSoon && isAuthenticated && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm">
                  Session expires in {formatTimeUntilExpiry()}
                </div>
              )}

              <div className="border-t border-white/[0.06] my-2 pt-2 flex flex-col gap-1.5">
                {isAuthenticated ? (
                  <>
                    <Link
                      to={profilePath}
                      onClick={() => setMobileMenuOpen(false)}
                      aria-current={isActive(profilePath) ? 'page' : undefined}
                      className={`${isActive(profilePath) ? 'nav-link-active' : 'nav-link-inactive'} w-full`}
                    >
                      Public profile
                    </Link>
                    <Link
                      to="/passport"
                      onClick={() => setMobileMenuOpen(false)}
                      className="nav-link-inactive w-full"
                    >
                      Bilt:Spec Passport
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="nav-link-inactive w-full"
                    >
                      Settings
                    </Link>
                    <Link
                      to="/settings/billing"
                      onClick={() => setMobileMenuOpen(false)}
                      className="nav-link-inactive w-full"
                    >
                      Bilt:Spec Premium
                    </Link>
                    <button
                      type="button"
                      data-testid="logout-button-mobile"
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="nav-link-inactive w-full text-left"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      data-testid="login-button-menu"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-secondary w-full min-h-[44px]"
                    >
                      Sign in
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/register');
                        setMobileMenuOpen(false);
                      }}
                      className="btn-primary w-full min-h-[44px]"
                    >
                      Start your garage
                    </button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default AppHeader;
